import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Modal,
  Button,
  Card,
  Space,
  Typography,
  Input,
  Spin,
  Image,
  Empty,
  Row,
  Col,
  Popconfirm,
  message,
  Tag,
  Divider,
  Tooltip,
  Alert,
  Slider,
} from 'antd';
import {
  PictureOutlined,
  DeleteOutlined,
  CheckOutlined,
  ReloadOutlined,
  BulbOutlined,
  UserOutlined,
  LinkOutlined,
  FieldTimeOutlined,
  RobotOutlined,
  EnvironmentOutlined,
  SyncOutlined,
  PlusOutlined,
  PlayCircleOutlined,
  VideoCameraOutlined,
  ThunderboltOutlined,
} from '@ant-design/icons';
import { SourcePanel, SourceCharacter } from '../../types';
import { ShotModeDecision } from '../../types/multiShot';
import { sourceStoriesApi } from '../../services/api';
import { useApi, useAsyncOperation, useThemeColors } from '../../hooks';
import { formatDate } from '../../utils';
import VisualReferencePlanModal from './VisualReferencePlanModal';
import ManualReferenceModal from './ManualReferenceModal';
import VideoGenerationPreview from './VideoGenerationPreview';
import VideoGenerationCard from '../../components/VideoGenerationCard';
import VideoHistoryDrawer from '../../components/VideoHistoryDrawer';
import { visualPlanRefPreviewUrls } from '../../utils/panelReferenceImages';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

interface PanelImageModalProps {
  visible: boolean;
  panel: SourcePanel;
  storyId: string;
  onClose: () => void;
  onRefresh?: (updatedPanel: SourcePanel) => void;
  getCharacterName?: (id: string) => string;
}

const PanelImageModal: React.FC<PanelImageModalProps> = ({
  visible,
  panel,
  storyId,
  onClose,
  onRefresh,
  getCharacterName,
}) => {
  const themeColors = useThemeColors();
  const [localPanel, setLocalPanel] = useState<SourcePanel>(panel);
  const [originalPrompt, setOriginalPrompt] = useState(panel.visualDescription || '');
  const [refinedScript, setRefinedScript] = useState(panel.refinedVisualDescription || '');
  const [technicalPrompt, setTechnicalPrompt] = useState(panel.technicalPrompt || '');
  const [motionPrompt, setMotionPrompt] = useState(panel.motionPrompt || '');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isExtractingEndFrame, setIsExtractingEndFrame] = useState(false);
  const [isRefining, setIsRefining] = useState(false);
  const [isPlanning, setIsPlanning] = useState(false);
  const [isVideoPreviewVisible, setIsVideoPreviewVisible] = useState(false);
  const [visualPlan, setVisualPlan] = useState<any>(panel.visualPlan);
  const [characters, setCharacters] = useState<SourceCharacter[]>([]);
  const [loadingCharacters, setLoadingCharacters] = useState(false);

  // For manual reference selection
  const [isManualRefVisible, setIsManualRefVisible] = useState(false);
  const [isAddingRef, setIsAddingRef] = useState(false);

  // For video history
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);

  // Fetch all hierarchy data for panel picker (can be optimized but simplified for now)
  const { data: processesData } = useApi(useCallback(() => sourceStoriesApi.getSourceProcesses(storyId), [storyId]), { immediate: visible });
  const { data: scenesData } = useApi(useCallback(() => sourceStoriesApi.getAllSourceScenes(storyId), [storyId]), { immediate: visible });
  const { data: panelsData } = useApi(useCallback(() => sourceStoriesApi.getAllSourcePanels(storyId), [storyId]), { immediate: visible });

  // For visual plan modal integration (optional, if we still want to see the full list)
  const [isReferencePlanVisible, setIsReferencePlanVisible] = useState(false);

  // Calculate default process and scene for manual reference (suggest previous scene)
  const previousSceneContext = useMemo(() => {
    if (!visible || !panel || !processesData?.data || !scenesData?.data) return null;

    const processes = [...processesData.data].sort((a, b) => a.orderIndex - b.orderIndex);
    const scenes = [...scenesData.data].sort((a, b) => a.orderIndex - b.orderIndex);

    const currentScene = scenes.find(s => s.id === panel.sceneId);
    if (!currentScene) return null;

    const currentProcess = processes.find(p => p.id === currentScene.processId);
    if (!currentProcess) return null;

    // Find scenes in current process
    const processScenes = scenes.filter(s => s.processId === currentProcess.id);
    const currentSceneIdx = processScenes.findIndex(s => s.id === currentScene.id);

    if (currentSceneIdx > 0) {
      // Previous scene in same process
      return {
        processId: currentProcess.id,
        sceneId: processScenes[currentSceneIdx - 1].id
      };
    } else {
      // First scene in process, go to previous process
      const currentProcessIdx = processes.findIndex(p => p.id === currentProcess.id);
      if (currentProcessIdx > 0) {
        const prevProcess = processes[currentProcessIdx - 1];
        const prevProcessScenes = scenes.filter(s => s.processId === prevProcess.id);
        if (prevProcessScenes.length > 0) {
          return {
            processId: prevProcess.id,
            sceneId: prevProcessScenes[prevProcessScenes.length - 1].id
          };
        }
      }
    }

    // Default to current if no previous found
    return {
      processId: panel.processId,
      sceneId: panel.sceneId
    };
  }, [visible, panel, processesData, scenesData]);

  const { execute: executeAsync } = useAsyncOperation();

  // Reset local state when panel changes or modal opens
  useEffect(() => {
    if (visible && panel) {
      setLocalPanel(panel);
      setOriginalPrompt(panel.visualDescription || '');
      setRefinedScript(panel.refinedVisualDescription || '');
      setTechnicalPrompt(panel.technicalPrompt || '');
      setMotionPrompt(panel.motionPrompt || '');
      setVisualPlan(panel.visualPlan);
    }
  }, [visible, panel]);

  // Fetch panel images
  const {
    data: imagesData,
    loading: imagesLoading,
    refresh: refreshImages,
  } = useApi(
    useCallback(
      () => (visible ? sourceStoriesApi.getPanelImages(storyId, panel.sceneId, panel.id) : Promise.resolve({ data: [] })),
      [storyId, panel.sceneId, panel.id, visible]
    ),
    { immediate: visible }
  );

  // Auto-poll khi có ảnh đang generating (generate ảnh chạy nền) -> tự refresh khi xong, khỏi F5
  const imagesPollingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  useEffect(() => {
    const hasGenerating = (imagesData?.data || []).some((img: any) => img.status === 'generating');
    if (hasGenerating && visible) {
      if (!imagesPollingRef.current) {
        imagesPollingRef.current = setInterval(() => refreshImages(), 5000);
      }
    } else if (imagesPollingRef.current) {
      clearInterval(imagesPollingRef.current);
      imagesPollingRef.current = null;
    }
    return () => {
      if (imagesPollingRef.current) {
        clearInterval(imagesPollingRef.current);
        imagesPollingRef.current = null;
      }
    };
  }, [imagesData, visible, refreshImages]);

  // Fetch characters in this panel
  const fetchCharacters = useCallback(async () => {
    if (!visible || !panel.characterIds || panel.characterIds.length === 0) return;
    
    setLoadingCharacters(true);
    try {
      const response = await sourceStoriesApi.getSourceCharacters(storyId, { pageSize: 100 });
      const items = response.data || []; 
      const panelCharacters = items.filter((c: SourceCharacter) => panel.characterIds.includes(c.id)).sort((a: SourceCharacter, b: SourceCharacter) => {
        // Convert both values to Date objects and then compare
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return dateA - dateB;
      });
      setCharacters(panelCharacters);
    } catch (error) {
      console.error('Failed to fetch characters:', error);
    } finally {
      setLoadingCharacters(false);
    }
  }, [visible, panel.characterIds, storyId]);

  useEffect(() => {
    fetchCharacters();
  }, [fetchCharacters]);

  const handleAutoGenerateAvatar = async (characterId: string) => {
    await executeAsync(
      async () => {
        await sourceStoriesApi.autoGenerateCharacterAvatar(storyId, characterId);
        fetchCharacters();
      },
      { successMessage: 'Character avatar generated and set!' }
    );
  };

  const handleCheckPlan = async () => {
    setIsPlanning(true);
    try {
      const res = await sourceStoriesApi.checkVisualReferences(storyId, panel.sceneId, panel.id);
      setVisualPlan(res.data);
      message.success('AI Visual Plan generated and saved!');
    } catch (error) {
      message.error('Failed to generate visual plan');
    } finally {
      setIsPlanning(false);
    }
  };

  const handleRemoveRef = async (sourceId: string) => {
    try {
      const res = await sourceStoriesApi.removeReference(storyId, panel.sceneId, panel.id, sourceId);
      setVisualPlan(res.data);
      message.success('Reference removed!');
    } catch (error) {
      message.error('Failed to remove reference');
    }
  };

  const handleAddManualRef = async (sourcePanelId: string) => {
    setIsAddingRef(true);
    try {
      const res = await sourceStoriesApi.addManualReference(storyId, panel.sceneId, panel.id, sourcePanelId);
      setVisualPlan(res.data);
      message.success('Manual reference analyzed and added!');
      setIsManualRefVisible(false); // Close modal after success as requested
    } catch (error) {
      message.error('Failed to add manual reference');
    } finally {
      setIsAddingRef(false);
    }
  };

  const handleRefineScript = async () => {
    if (!visualPlan) {
      message.warning('Please generate a Visual Plan first.');
      return;
    }

    setIsRefining(true);
    try {
      // First, update original prompt if it changed
      if (originalPrompt !== panel.visualDescription) {
        await sourceStoriesApi.updateSourcePanel(storyId, panel.sceneId, panel.id, {
          visualDescription: originalPrompt
        });
      }

      const response = await sourceStoriesApi.refinePanelVisualPrompt(storyId, panel.sceneId, panel.id);
      setRefinedScript(response.data.refinedPrompt);
      if (response.data.technicalPrompt) {
        setTechnicalPrompt(response.data.technicalPrompt);
      }
      if (response.data.motionPrompt) {
        setMotionPrompt(response.data.motionPrompt);
      }
      message.success('Director Script & Technical Prompt generated by AI!');
    } catch (error: any) {
      message.error(error.message || 'Failed to refine script');
    } finally {
      setIsRefining(false);
    }
  };

  const handleGenerateImage = async () => {
    if (!technicalPrompt.trim()) {
      message.warning('Technical prompt is required for image generation.');
      return;
    }

    setIsGenerating(true);
    try {
      // Sync manual changes to backend before generation
      await sourceStoriesApi.updateSourcePanel(storyId, panel.sceneId, panel.id, {
        refinedVisualDescription: refinedScript,
        technicalPrompt: technicalPrompt,
        motionPrompt: motionPrompt
      });

      await sourceStoriesApi.generatePanelImage(storyId, panel.sceneId, panel.id, technicalPrompt);
      message.success('Image generated successfully!');
      refreshImages();
    } catch (error: any) {
      message.error(error.message || 'Failed to generate image');
    } finally {
      setIsGenerating(false);
    }
  };



  const handleExtractEndFrame = async () => {
    setIsExtractingEndFrame(true);
    try {
      const response = await sourceStoriesApi.extractPanelEndFrame(storyId, localPanel.sceneId, localPanel.id);
      const updatedPanel = response.data;
      setLocalPanel(updatedPanel);
      if (onRefresh) {
        onRefresh(updatedPanel);
      }
      message.success('End frame extracted successfully!');
    } catch (error: any) {
      message.error(error.message || 'Failed to extract end frame');
    } finally {
      setIsExtractingEndFrame(false);
    }
  };

  const handleSetAsActive = async (imageId: string) => {
    await executeAsync(
      async () => {
        const response = await sourceStoriesApi.setPanelImage(storyId, localPanel.sceneId, localPanel.id, imageId);
        const updatedPanel = response.data;
        setLocalPanel(updatedPanel);
        if (onRefresh) {
          onRefresh(updatedPanel);
        }
      },
      { successMessage: 'Panel image updated successfully!' }
    );
    refreshImages();
  };

  const handleDeleteImage = async (imageId: string) => {
    await executeAsync(
      () => sourceStoriesApi.deletePanelImage(storyId, localPanel.sceneId, localPanel.id, imageId),
      { successMessage: 'Image deleted successfully!' }
    );
    refreshImages();
  };

  const images = imagesData?.data || [];
  const hasImages = images.length > 0;
  const allCharactersHaveAvatars = characters.length > 0 && characters.every(c => !!c.avatarUrl);

  const getReferenceIcon = (type: string) => {
    switch (type) {
        case 'IDENTITY': return <UserOutlined />;
        case 'OUTFIT': return <PictureOutlined />;
        case 'ENVIRONMENT': return <EnvironmentOutlined />;
        default: return <LinkOutlined />;
    }
  };

  const getReferenceColor = (type: string) => {
    switch (type) {
        case 'IDENTITY': return 'blue';
        case 'OUTFIT': return 'purple';
        case 'ENVIRONMENT': return 'green';
        default: return 'default';
    }
  };

  const isProcessing = isGenerating || isRefining || isPlanning;

  return (
    <Modal
      title={
        <Space>
          <span>Panel Image Management - #${panel.sceneOrderIndex}</span>
          {localPanel.mediaType === 'VIDEO' ? (
            <Tag color="purple" icon={<VideoCameraOutlined />}>VIDEO</Tag>
          ) : (
            <Tag color="cyan" icon={<PictureOutlined />}>IMAGE</Tag>
          )}
          {localPanel.estimatedAudioDuration && (
            <Tag color="orange">~{localPanel.estimatedAudioDuration}s audio</Tag>
          )}
          {panel.visualPlan && (
            <Tooltip title="Xem kế hoạch tham chiếu (Visual Plan)">
              <Button 
                size="small" 
                type="text" 
                icon={<BulbOutlined style={{ color: '#faad14' }} />} 
                onClick={() => setIsReferencePlanVisible(true)}
              />
            </Tooltip>
          )}
        </Space>
      }
      open={visible}
      onCancel={isProcessing ? undefined : onClose}
      footer={null}
      width={1100}
      centered
      maskClosable={false}
      keyboard={false}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 160px)',
          overflowY: 'auto',
          overflowX: 'hidden',
          padding: '24px'
        }
      }}
    >
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        {/* Caption & Reference */}
        <Card size="small" title="Panel Content Reference">
          <div style={{ padding: '8px', background: themeColors.bgSuccess, borderRadius: 4, border: `1px solid ${themeColors.borderSuccess}`, marginBottom: 12, color: themeColors.textPrimary }}>
            <Text strong>"{panel.localizedCaption || panel.caption}"</Text>
          </div>
          
          <Divider orientation={"left" as any} style={{ margin: '12px 0' }}>Character Identity Materials</Divider>
          
          {loadingCharacters ? (
            <Spin size="small" />
          ) : characters.length > 0 ? (
            <Row gutter={[16, 16]}>
              {characters.map(char => (
                <Col key={char.id} span={8}>
                  <Card size="small" style={{ background: char.avatarUrl ? themeColors.bgPrimary : themeColors.bgError }}>
                    <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Space>
                        <div style={{ width: 45, aspectRatio: '9 / 16', background: themeColors.bgTertiary, borderRadius: 4, overflow: 'hidden', flexShrink: 0 }}>
                          {char.avatarUrl ? (
                            <img src={char.avatarUrl} alt={char.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                          ) : (
                            <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              <UserOutlined style={{ fontSize: 16, color: '#bfbfbf' }} />
                            </div>
                          )}
                        </div>
                        <div style={{ maxWidth: 100 }}>
                          <Text strong ellipsis>{char.name}</Text>
                          <br />
                          <Text type="secondary" style={{ fontSize: '11px' }}>
                            {char.avatarUrl ? 'Ready' : 'No avatar'}
                          </Text>
                        </div>
                      </Space>
                      {!char.avatarUrl && (
                        <Button 
                          size="small" 
                          type="primary" 
                          ghost 
                          icon={<BulbOutlined />}
                          onClick={() => handleAutoGenerateAvatar(char.id)}
                        >
                          Auto Gen
                        </Button>
                      )}
                    </Space>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <Text type="secondary">No characters assigned to this panel.</Text>
          )}
        </Card>

        {/* Step 1: Visual Prompting Pipeline */}
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Card 
            title="1. Original Visual Prompt (Concept)"
            size="small"
          >
            <TextArea
              rows={3}
              placeholder="Sơ lược về bối cảnh, trang phục, hành động..."
              value={originalPrompt}
              onChange={(e) => setOriginalPrompt(e.target.value)}
              style={{ marginBottom: 12 }}
            />

            {/* Visual Plan Summary Section */}
            {visualPlan ? (
              <div style={{ 
                background: themeColors.bgSecondary, 
                border: `1px solid ${themeColors.bgTertiary}`, 
                borderRadius: '6px', 
                padding: '12px',
                color: themeColors.textPrimary,
                marginBottom: 16
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <Space>
                    <BulbOutlined style={{ color: '#faad14' }} />
                    <Text strong style={{ fontSize: '13px' }}>AI Visual Plan Summary:</Text>
                  </Space>
                  <Space>
                    <Tag 
                      icon={<FieldTimeOutlined />} 
                      color={visualPlan.temporalStatus === 'CONTINUOUS' ? 'cyan' : 'orange'}
                      style={{ margin: 0 }}
                    >
                      {visualPlan.temporalStatus === 'CONTINUOUS' ? 'Tiếp nối' : 'Bước nhảy'}
                    </Tag>
                    <Button 
                      size="small" 
                      type="link" 
                      icon={<SyncOutlined spin={isPlanning} />} 
                      onClick={handleCheckPlan}
                      loading={isPlanning}
                      style={{ padding: 0, height: 'auto' }}
                    >
                      Re-plan
                    </Button>
                  </Space>
                </div>
                
                <Paragraph style={{ fontSize: '12px', marginBottom: 12, color: '#666' }} italic>
                  "{visualPlan.planningLogic}"
                </Paragraph>
                
                  {/* Reference Details List */}
                  <div style={{ marginTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                      <Text strong style={{ fontSize: '14px' }}>Reference Details:</Text>
                      <Button 
                        size="small" 
                        type="dashed" 
                        icon={<PlusOutlined />} 
                        onClick={() => setIsManualRefVisible(true)}
                      >
                        Thêm tham chiếu
                      </Button>
                    </div>
                    
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                      {[...(visualPlan.references || [])].sort((a: any, b: any) => {
                        const aTypes = a.types || (a.type ? [a.type] : []);
                        const bTypes = b.types || (b.type ? [b.type] : []);
                        const aIsIdentity = aTypes.includes('IDENTITY');
                        const bIsIdentity = bTypes.includes('IDENTITY');
                        if (aIsIdentity && !bIsIdentity) return 1;
                        if (!aIsIdentity && bIsIdentity) return -1;
                        return 0;
                      }).map((ref: any, idx: number) => {
                        const types = ref.types || [ref.type];
                        const isIdentity = types.includes('IDENTITY');
                        const previewUrls = visualPlanRefPreviewUrls(ref);
                        return (
                          <Card 
                            key={idx} 
                            size="small" 
                            bodyStyle={{ padding: '12px' }}
                            style={{ 
                              border: '1px solid #f0f0f0', 
                              borderRadius: '8px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.02)'
                            }}
                          >
                            <Row gutter={16} align="top" wrap={false}>
                              <Col flex="80px">
                                <div style={{ 
                                  display: 'flex',
                                  gap: 4,
                                  width: 80, 
                                  height: 142,
                                  background: themeColors.bgTertiary,
                                  borderRadius: 4,
                                  overflow: 'hidden',
                                  border: `1px solid ${themeColors.bgTertiary}`
                                }}>
                                  {previewUrls.length > 0 ? (
                                    previewUrls.map((url, pi) => (
                                      <div key={pi} style={{ flex: 1, minWidth: 0, height: '100%' }}>
                                        <Image 
                                          src={url} 
                                          alt={ref.label} 
                                          style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                                          preview={{ mask: <Text style={{ color: '#fff', fontSize: '10px' }}>Xem</Text> }}
                                        />
                                      </div>
                                    ))
                                  ) : (
                                    <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                      <PictureOutlined style={{ fontSize: 24, color: '#bfbfbf' }} />
                                    </div>
                                  )}
                                </div>
                              </Col>
                              <Col flex="auto">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                                  <Space direction="vertical" size={4}>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                                      {types.map((t: string) => (
                                        <Tag key={t} icon={getReferenceIcon(t)} color={getReferenceColor(t)} style={{ margin: 0 }}>
                                          {t}
                                        </Tag>
                                      ))}
                                    </div>
                                    <Text strong style={{ fontSize: '15px' }}>{ref.label}</Text>
                                  </Space>
                                  {!isIdentity && (
                                    <Popconfirm
                                      title="Xóa tham chiếu này?"
                                      onConfirm={() => handleRemoveRef(ref.sourceId)}
                                      okText="Xóa"
                                      cancelText="Hủy"
                                    >
                                      <Button 
                                        size="small" 
                                        type="text" 
                                        danger 
                                        icon={<DeleteOutlined />} 
                                      >
                                        Gỡ bỏ
                                      </Button>
                                    </Popconfirm>
                                  )}
                                </div>
                                <div style={{ 
                                  background: themeColors.bgSecondary, 
                                  padding: '8px 12px', 
                                  borderRadius: '4px',
                                  borderLeft: `3px solid ${themeColors.bgTertiary}`,
                                  color: themeColors.textPrimary
                                }}>
                                  <Text type="secondary" style={{ fontSize: '13px', lineHeight: '1.5' }}>
                                    {ref.reason}
                                  </Text>
                                </div>
                                {ref.sourceInfo && (
                                  <div style={{ marginTop: 8 }}>
                                    <Text type="secondary" style={{ fontSize: '11px' }}>
                                      <LinkOutlined /> Nguồn: {ref.sourceInfo}
                                    </Text>
                                  </div>
                                )}
                              </Col>
                            </Row>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
              </div>
            ) : (
              <div style={{ 
                textAlign: 'center', 
                padding: '16px', 
                background: themeColors.bgWarning, 
                borderRadius: '8px',
                color: themeColors.textPrimary,
                border: '1px dashed #ffd591', 
                marginBottom: 16
              }}>
                <Text type="warning" style={{ fontSize: '13px', display: 'block', marginBottom: 8 }}>
                  Chưa có kế hoạch tham chiếu. AI cần lập kế hoạch trước khi tinh chỉnh nội dung.
                </Text>
                <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<BulbOutlined />} 
                    onClick={handleCheckPlan}
                    loading={isPlanning}
                    style={{ 
                      background: '#722ed1', 
                      borderColor: '#722ed1',
                      boxShadow: '0 2px 4px rgba(114, 46, 209, 0.2)',
                      fontWeight: 500
                    }}
                  >
                    Lập kế hoạch tham chiếu (AI Planning)
                  </Button>
                  <Button 
                    size="small" 
                    icon={<PlusOutlined />} 
                    onClick={() => setIsManualRefVisible(true)}
                    style={{ 
                      borderRadius: '4px'
                    }}
                  >
                    Thêm tham chiếu thủ công
                  </Button>
                </div>
              </div>
            )}

            <div style={{ textAlign: 'center' }}>
              <Button 
                type="primary" 
                size="middle"
                icon={<RobotOutlined />} 
                onClick={handleRefineScript}
                loading={isRefining}
                disabled={!visualPlan || (!allCharactersHaveAvatars && characters.length > 0)}
                style={{ 
                  background: visualPlan ? '#1890ff' : '#d9d9d9', 
                  borderColor: visualPlan ? '#1890ff' : '#d9d9d9',
                  fontWeight: 500,
                  boxShadow: visualPlan ? '0 2px 4px rgba(24,144,255,0.2)' : 'none'
                }}
              >
                Refine Script & Generate Technical Prompt (AI)
              </Button>
            </div>
          </Card>

          <Row gutter={16}>
            <Col span={localPanel.mediaType === 'VIDEO' ? 8 : 12}>
              <Card title="2. Refined Visual Script (VI)" size="small">
                <TextArea
                  rows={8}
                  placeholder="Mô tả chi tiết bằng tiếng Việt (Director's Script)..."
                  value={refinedScript}
                  onChange={(e) => setRefinedScript(e.target.value)}
                  style={{ fontSize: '12px' }}
                />
              </Card>
            </Col>
            <Col span={localPanel.mediaType === 'VIDEO' ? 8 : 12}>
              <Card title="3. Technical Prompt (EN)" size="small">
                <TextArea
                  rows={8}
                  placeholder="Technical prompt in English for Image Generator..."
                  value={technicalPrompt}
                  onChange={(e) => setTechnicalPrompt(e.target.value)}
                  style={{ fontSize: '12px', fontFamily: 'monospace' }}
                />
              </Card>
            </Col>
            {localPanel.mediaType === 'VIDEO' && (
              <Col span={8}>
                <Card title="4. Motion Prompt (EN)" size="small">
                  <TextArea
                    rows={8}
                    placeholder="Motion instructions for Kling AI (5-layer formula)..."
                    value={motionPrompt}
                    onChange={(e) => setMotionPrompt(e.target.value)}
                    style={{ fontSize: '12px', fontFamily: 'monospace' }}
                  />
                </Card>
              </Col>
            )}
          </Row>

          <Button
            type="primary"
            size="large"
            icon={<PictureOutlined />}
            onClick={handleGenerateImage}
            loading={isGenerating}
            disabled={!technicalPrompt.trim() || (characters.length > 0 && !allCharactersHaveAvatars)}
            block
          >
            {isGenerating ? 'Producing Image...' : 'Generate Panel Image'}
          </Button>
        </Space>

        {/* Image Gallery */}
        <Card 
          title={`Generated Images (${images.length})`} 
          size="small"
          extra={
            hasImages && (
              <Button
                icon={<ReloadOutlined />}
                onClick={refreshImages}
                loading={imagesLoading}
              >
                Refresh
              </Button>
            )
          }
        >
          {imagesLoading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <Spin />
            </div>
          ) : !hasImages ? (
            <Empty
              description="No images yet. Prepare your prompt and generate your first image above."
              style={{ padding: '40px 0' }}
            />
          ) : (
            <Row gutter={[16, 16]}>
              {images.map((image) => (
                <Col span={6} key={image.id}>
                  <div style={{ position: 'relative', border: '1px solid #f0f0f0', borderRadius: 8, overflow: 'hidden' }}>
                    <div style={{ width: '100%', aspectRatio: '9 / 16', overflow: 'hidden', backgroundColor: themeColors.bgTertiary }}>
                      <Image
                        src={image.url}
                        alt="Panel"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        preview={{ mask: 'Preview' }}
                      />
                    </div>
                    
                    {localPanel.mediaType === 'VIDEO' ? (
                      localPanel.firstFrameUrl === image.url && (
                        <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                          <Tag color="purple" style={{ margin: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>First Frame</Tag>
                        </div>
                      )
                    ) : (
                      localPanel.imagePath === image.url && (
                        <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                          <Tag color="green" style={{ margin: 0, boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>Active</Tag>
                        </div>
                      )
                    )}

                    <div style={{ 
                      position: 'absolute', 
                      bottom: 0, 
                      left: 0, 
                      right: 0, 
                      padding: '8px', 
                      background: 'rgba(255,255,255,0.9)', 
                      backdropFilter: 'blur(4px)',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      borderTop: '1px solid #f0f0f0'
                    }}>
                      <Space size={4}>
                        <Button
                          type={(localPanel.mediaType === 'VIDEO' ? localPanel.firstFrameUrl === image.url : localPanel.imagePath === image.url) ? 'primary' : 'text'}
                          size="small"
                          icon={<CheckOutlined />}
                          onClick={() => handleSetAsActive(image.id)}
                          disabled={localPanel.mediaType === 'VIDEO' ? localPanel.firstFrameUrl === image.url : localPanel.imagePath === image.url}
                          title={localPanel.mediaType === 'VIDEO' ? "Đặt làm First Frame" : "Select as Active"}
                        />
                        <Popconfirm
                          title="Delete this image?"
                          onConfirm={() => handleDeleteImage(image.id)}
                        >
                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            title="Delete"
                          />
                        </Popconfirm>
                      </Space>
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        {formatDate(image.createdAt)}
                      </Text>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}
        </Card>

        {/* Step 2: Video Generation (if applicable) */}
        {(localPanel.mediaType === 'VIDEO' || localPanel.videoUrl) && (
          <VideoGenerationCard
            panel={localPanel}
            storyId={storyId}
            sceneId={localPanel.sceneId}
            onVideoGenerated={(updatedPanel) => {
              setLocalPanel(updatedPanel);
              if (onRefresh) {
                onRefresh(updatedPanel);
              }
            }}
            onOpenHistory={() => setIsHistoryVisible(true)}
            onVideoPreview={() => setIsVideoPreviewVisible(true)}
            isExtractingEndFrame={isExtractingEndFrame}
            onExtractEndFrame={handleExtractEndFrame}
          />
        )}
      </Space>

      {isReferencePlanVisible && (
        <VisualReferencePlanModal
          visible={isReferencePlanVisible}
          panel={panel}
          storyId={storyId}
          onClose={() => setIsReferencePlanVisible(false)}
        />
      )}

      {isManualRefVisible && (
        <ManualReferenceModal
          visible={isManualRefVisible}
          onClose={() => setIsManualRefVisible(false)}
          onSelect={handleAddManualRef}
          processes={processesData?.data || []}
          scenes={scenesData?.data || []}
          panels={panelsData?.data || []}
          loading={isAddingRef}
          defaultProcessId={previousSceneContext?.processId || panel.processId}
          defaultSceneId={previousSceneContext?.sceneId || panel.sceneId}
        />
      )}

      {/* Video Preview Modal */}
      <Modal
        open={isVideoPreviewVisible}
        onCancel={() => setIsVideoPreviewVisible(false)}
        footer={null}
        width={400}
        centered
        destroyOnClose
        bodyStyle={{ padding: 0, background: '#000', borderRadius: '8px', overflow: 'hidden' }}
      >
        <div style={{ width: '100%', aspectRatio: '9/16', background: '#000' }}>
          <video 
            src={localPanel.videoUrl} 
            controls 
            autoPlay 
            style={{ width: '100%', height: '100%' }}
          />
        </div>
      </Modal>

      {/* Video History Drawer */}
      <VideoHistoryDrawer
        visible={isHistoryVisible}
        panel={localPanel}
        storyId={storyId}
        sceneId={localPanel.sceneId}
        onClose={() => setIsHistoryVisible(false)}
        onVideoApplied={(updatedPanel) => {
          setLocalPanel(updatedPanel);
          if (onRefresh) {
            onRefresh(updatedPanel);
          }
        }}
      />
    </Modal>
  );
};

export default PanelImageModal;
